import { Component, createContext, useContext, useEffect, useRef } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { analytics } from "./posthog-adapter";
import type { AnalyticsClient, AnalyticsConfig } from "./types";

const AnalyticsContext = createContext<AnalyticsClient>(analytics);

export interface AnalyticsProviderProps {
  config: AnalyticsConfig;
  /**
   * Whether analytics may initialise. Defaults to `true` (unchanged behaviour). Pass a cookie-consent
   * grant here to hold PostHog back until the user accepts: no network request and no cookie are made
   * until `enabled` is true; withdrawing it (true → false) opts out for the rest of the session.
   */
  enabled?: boolean;
  children: React.ReactNode;
}

/**
 * AnalyticsProvider — initialises the analytics client once `enabled`, and exposes it via context.
 * Context defaults to the singleton, so the hooks below are safe even before init (every method no-ops
 * until `init` has run).
 */
export function AnalyticsProvider({ config, enabled = true, children }: AnalyticsProviderProps) {
  const initialized = useRef(false);
  useEffect(() => {
    if (enabled && !initialized.current) {
      analytics.init(config);
      initialized.current = true;
    } else if (!enabled && initialized.current) {
      analytics.optOut();
    }
  }, [enabled, config]);
  return <AnalyticsContext.Provider value={analytics}>{children}</AnalyticsContext.Provider>;
}

export function useAnalytics(): AnalyticsClient {
  return useContext(AnalyticsContext);
}

/**
 * usePageViews — captures a "$pageview" whenever the given path changes.
 * In the SPA pass the React Router location; in Astro pass the current pathname.
 */
export function usePageViews(path: string): void {
  const client = useAnalytics();
  useEffect(() => {
    client.capture("$pageview", { path });
  }, [client, path]);
}

export interface AppErrorBoundaryProps {
  children: ReactNode;
  /** Rendered instead of the broken tree; a minimal built-in fallback is the default. */
  fallback?: ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
}

/**
 * Top-level error boundary (ADR-0015): replaces the white screen and reports the render error
 * through the AnalyticsClient port — no vendor SDK outside this package. Place it directly
 * inside AnalyticsProvider so provider failures further down are still caught.
 */
export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  static override contextType = AnalyticsContext;
  declare context: AnalyticsClient;

  override state: AppErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.context.captureException(error, {
      boundary: "app",
      componentStack: info.componentStack,
    });
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          // Deliberately untranslated: the i18n provider may be part of the broken tree.
          <main role="alert" style={{ padding: "2rem", fontFamily: "sans-serif" }}>
            <h1>Something went wrong / Une erreur est survenue</h1>
            <p>
              <a href="/">Reload / Recharger</a>
            </p>
          </main>
        )
      );
    }
    return this.props.children;
  }
}
